// src/components/BCP/AIAnalysisPanel.tsx - Refactored to use hooks and utilities
import React from 'react';
import { FormState } from '../../types/bcpForm';
import { useAIAnalysis } from '../../hooks/useAIAnalysis';
import { getInsightIconConfig, getPriorityColor, getCompletionScoreColor, getCompletionScoreBgColor, sortInsightsByPriority } from '../../utils/aiAnalysisUtils';
import { getStepContent, getStepTitle, getStepDescription, getFilteredNextSteps } from '../../utils/stepContent';

export interface AIAnalysisPanelProps {
  formData: FormState;
  currentStep: number;
  isVisible: boolean;
  risks?: { primary_concerns: string[]; location_risks: string[] };
  employees?: any[];
  bcpId?: string;
  isEditing?: boolean;
}

const AIAnalysisPanel: React.FC<AIAnalysisPanelProps> = ({
  formData,
  currentStep,
  isVisible,
  risks,
  employees,
  bcpId,
  isEditing
}) => {
  const {
    currentStepAnalysis,
    isAnalyzing,
    shouldShowAnalysis
  } = useAIAnalysis(formData, currentStep, isVisible, risks, employees, bcpId, isEditing);

  if (!isVisible) return null;

  const showAnalysis = shouldShowAnalysis(currentStep, formData, risks, employees);

  // Get step-specific content
  const stepTitle = getStepTitle(currentStep);
  const stepDescription = getStepDescription(currentStep);
  const stepNextSteps = getFilteredNextSteps(currentStep, formData, risks, employees);

  return (
    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
      {/* RULE 1: Header Alignment - Step Context */}
      <div className="flex items-center mb-4">
        <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-lg font-semibold text-gray-900">{stepTitle}</h3>
          <p className="text-sm text-gray-600">{stepDescription}</p>
        </div>
      </div>

      {/* RULE 2: Progress Indicator */}
      {currentStepAnalysis && showAnalysis && (
        <div className="p-3 mb-4 bg-white rounded-lg border border-blue-200">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Form Completion</span>
            <span className={`text-sm font-semibold ${getCompletionScoreColor(currentStepAnalysis.completion_score)}`}>
              {currentStepAnalysis.completion_score}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getCompletionScoreBgColor(currentStepAnalysis.completion_score)}`}
              style={{ width: `${currentStepAnalysis.completion_score}%` }}
            />
          </div>
        </div>
      )}

      {/* Loading State */}
      {isAnalyzing && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full border-2 border-blue-600 border-t-transparent animate-spin"></div>
            <span className="text-sm text-gray-600">Analyzing your data...</span>
          </div>
        </div>
      )}

      {/* RULE 3: Step-Specific Insights - Only show current step insights */}
      {currentStepAnalysis && showAnalysis && !isAnalyzing && (
        <div className="space-y-4">
          {/* Insights */}
          {currentStepAnalysis.insights.length > 0 && (
            <div>
              <h4 className="mb-3 text-sm font-semibold text-gray-800">AI Insights</h4>
              <div className="space-y-2">
                {sortInsightsByPriority(currentStepAnalysis.insights).map((insight, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${getPriorityColor(insight.priority)}`}
                  >
                    <div className="flex items-start space-x-2">
                      <div className="flex-shrink-0 mt-0.5">
                        {(() => {
                          const iconConfig = getInsightIconConfig(insight.type);
                          return iconConfig ? (
                            <svg className={iconConfig.className} fill={iconConfig.fill} stroke={iconConfig.stroke} viewBox={iconConfig.viewBox}>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconConfig.path} />
                            </svg>
                          ) : null;
                        })()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                            {insight.category}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            insight.priority === 'high' ? 'bg-red-100 text-red-800' :
                            insight.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {insight.priority}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{insight.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RULE 4: Next Steps - Always at bottom with static content */}
          {stepNextSteps.length > 0 && (
            <div>
              <h4 className="mb-3 text-sm font-semibold text-gray-800">Next Steps</h4>
              <div className="space-y-2">
                {stepNextSteps.map((step, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="flex-shrink-0 w-2 h-2 mt-1.5 bg-blue-600 rounded-full"></div>
                    <span className="text-sm text-gray-700">{step.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* No Analysis State */}
      {!showAnalysis && !isAnalyzing && (
        <div className="text-center py-8">
          <div className="flex justify-center mb-3">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500">
            Complete the current step to see AI-powered insights
          </p>
        </div>
      )}
    </div>
  );
};

export default AIAnalysisPanel;